import { Button, Card, CardContent, Chip, Grid, IconButton, Skeleton, Tooltip, Paper } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { StyledCard } from '../AccountBasicInfo/AccountBacisInfo.styles';
import CustomDataGrid from '../DataGrid/DataGrid';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { PermissionsEnum } from "../../utils/auth.types";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import Link from '@mui/material/Link';
import { ApiStatus, apiLimit, defaultRowsPerPageOptions } from "../../utils/api.types";
import RemoveRedEyeTwoToneIcon from '@mui/icons-material/RemoveRedEyeTwoTone';

import { GridActionsCellItem, GridColDef, GridColumnHeaderParams, GridValueGetterParams } from '@mui/x-data-grid';
import NavigateBeforeOutlinedIcon from '@mui/icons-material/NavigateBeforeOutlined';


import { Inventory } from '../../utils/inventory.types';
import { fetchInventories } from '../../store/inventoryStore/inventoryThunk';
import { fetchEmployees, fetchPendingInvitations, resendInvitation, cancelInvitation } from '../../store/employeeStore/employeeThunk';

import moment from 'moment';
import { useNavigate } from "react-router-dom";
import CheckIcon from '@mui/icons-material/Check';
import CrossIcon from '@mui/icons-material/Close';
import { Utils } from '../../utils/common-utils';
import InfoIcon from '@mui/icons-material/Info';
import DeleteIcon from '@mui/icons-material/Delete';
import EmployeeFilters from '../Filters/EmployeeFilters';
import { ViewIcon } from 'lucide-react';
import { Employee } from '../../utils/employee.types';
import LoadingButton from '@mui/lab/LoadingButton';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import EmailIcon from '@mui/icons-material/Email';
import EmployeeInviteDialog from './EmployeeInviteDialog';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import { toast } from 'react-toastify';
import SendIcon from '@mui/icons-material/Send';
import CancelIcon from '@mui/icons-material/Cancel';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

interface EmployeeList {
    accountId?: string;
    inventoryId?: string;
}
const EmployeeListData: FC<EmployeeList> = (props) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { employees, status, rowCount } = useAppSelector(state => state.employees);
    const { permissions, selectedAccount } = useAppSelector(state => state.auth);

    const [open, setOpen] = useState(false);
    const [inventoryId, setInventory] = useState('');
    const [fetchData, setFetchData] = useState('');
    const canUpdateAccount = permissions?.includes(PermissionsEnum.Account_Update);

    const [filters, setFilters] = useState('');
    const [isRefersh, setIsRefersh] = useState(false);
    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [pendingInvitations, setPendingInvitations] = useState<any[]>([]);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(apiLimit.limit);
    const [pageQueryString, setPageQueryString] = useState('');
    const [pageSizeQueryString, setPageSizeQueryString] = useState(`&limit=${apiLimit.limit}`);

    /**
    * Handler for account name click.
    */
    const handleEditEmployeeClick = useCallback(({ _id }: Employee) => {
        //Call edit function
        let navLink = `/employee/edit/${_id}`;
        navigate(navLink);

    }, []);

    const handleViewEmployeeClick = useCallback(({ _id }: Employee) => {
        //Call view function
        let navLink = `/employee/view/${_id}`;
        navigate(navLink);

    }, []);

    /**
    * Handler for New Employee button click.
    */
    const handleAddEmployee = () => {
        const navLink = `/employee/create`;
        navigate(navLink);
    };

    /**
    * Handler for Invite Employee button click.
    */
    const handleInviteEmployee = () => {
        setInviteDialogOpen(true);
    };

    const resolveInvitationId = (invitations: any[], row: any) => {
        const employeeId = String(row?._id || '');
        const userId = String(row?.userId?._id || row?.userId || '');
        const employeeEmails = [
            row?.personalEmail,
            row?.businessEmail,
            row?.email,
            row?.userId?.email,
        ]
            .filter(Boolean)
            .map((value: string) => String(value).toLowerCase());

        const matchedInvitation = invitations.find((inv: any) => {
            const invitedEmployeeId = String(inv?.employeeId?._id || inv?.employeeId || '');
            const invitedUserId = String(inv?.userId?._id || inv?.userId || inv?.linkedUser?._id || inv?.linkedUser || '');
            const invitationEmails = [
                inv?.email,
                inv?.employeeId?.personalEmail,
                inv?.employeeId?.businessEmail,
                inv?.userId?.email,
                inv?.linkedUser?.email,
            ]
                .filter(Boolean)
                .map((value: string) => String(value).toLowerCase());

            const idMatched =
                (employeeId && invitedEmployeeId === employeeId) ||
                (userId && invitedUserId === userId);

            const emailMatched = employeeEmails.some((email: string) => invitationEmails.includes(email));

            return idMatched || emailMatched;
        });

        return matchedInvitation?._id || '';
    };

    const handleResendInvite = async (row: any) => {
        try {
            let invitationId = resolveInvitationId(pendingInvitations, row);

            if (!invitationId) {
                const pendingRes: any = await dispatch(fetchPendingInvitations()).unwrap();
                const latestPendingInvitations = pendingRes?.data || [];
                setPendingInvitations(latestPendingInvitations);
                invitationId = resolveInvitationId(latestPendingInvitations, row);
            }

            if (!invitationId) {
                toast.error("Pending invitation not found for this employee");
                return;
            }

            const res = await dispatch(resendInvitation(invitationId)).unwrap();
            toast.success(res?.message || "Invitation resent successfully");
            setIsRefersh(!isRefersh);
        } catch (error: any) {
            toast.error(error?.message || "Failed to resend invitation");
        }
    };

    const handleInviteDialogClose = () => {
        setInviteDialogOpen(false);
        // Refresh employee list to update invitation status
        setIsRefersh(!isRefersh);
    };

    /**
    * Handler for Salary Management button clicks - All navigate to salary list
    */
    const handleSalaryManagement = () => {
        const navLink = `/salary/list`;
        navigate(navLink);
    };

    /**
      * Fetch all the accounts on Accounts page load.
      */
    useEffect(() => {
        const { _id: id } = selectedAccount;
        const params = filters ? filters + pageQueryString + pageSizeQueryString : pageQueryString + pageSizeQueryString;
        if (filters) {
            dispatch(fetchEmployees({ accountId: id, type: '', params }));
        } else {
            dispatch(fetchEmployees({ accountId: id, type: '', params }));
        }
        setIsRefersh(true);
    }, [dispatch, selectedAccount, filters, isRefersh, page, pageSize, pageQueryString, pageSizeQueryString]);

    // Fetch pending invitations
    useEffect(() => {
        if (activeTab === 1) {
            dispatch(fetchPendingInvitations()).unwrap().then((data: any) => {
                setPendingInvitations(data?.data || []);
            }).catch(() => setPendingInvitations([]));
        }
    }, [dispatch, activeTab, isRefersh]);

    const handleResendInvitation = async (invitationId: string) => {
        try {
            const res: any = await dispatch(resendInvitation(invitationId)).unwrap();
            toast.success(res?.message || "Invitation resent successfully");
            setIsRefersh(!isRefersh);
        } catch (error: any) {
            toast.error(error?.message || "Failed to resend invitation");
        }
    };

    const handleCancelInvitation = async (invitationId: string) => {
        try {
            const res: any = await dispatch(cancelInvitation(invitationId)).unwrap();
            toast.success(res?.message || "Invitation cancelled");
            setIsRefersh(!isRefersh);
        } catch (error: any) {
            toast.error(error?.message || "Failed to cancel invitation");
        }
    };

    //Check status
    const checkStatus = useCallback((value) => {
        return (value == 'Active') ? <Chip label={<CheckIcon style={{ color: '#49a563', fontSize: 22, marginLeft: -5, marginTop: 5 }} />} style={{ color: '#49a563', backgroundColor: '#edf6ef', border: '1px solid #49a563', borderRadius: 25, width: 35, height: 35 }} />
            : <Chip label={<CrossIcon style={{ color: '#c33b3b', fontSize: 18, marginLeft: -9, marginTop: 7 }} />} style={{ color: '#c33b3b', backgroundColor: '#fbecec', border: '1px solid #c33b3b', borderRadius: 25, width: 25, height: 25 }} />;
    }, []);


    /** 
        * Column defs for account list data grid.
    */
    const columns: GridColDef[] = useMemo(() => [

        {
            field: "name",
            headerName: 'Name',
            type: "string",
            flex: 1, minWidth: 150,
            renderCell: ({ row, value }: GridValueGetterParams) => {

                return (
                    <p onClick={() => handleViewEmployeeClick(row)}>
                        {row.name}<br />
                        <span style={{ color: '#9da7b7' }}> {moment().diff(row.dob, 'years')} Year | {row.gender === 'male' ? 'Male' : row.gender === 'female' ? 'Female' : row.gender === 'other' ? 'Other' : ''}</span>
                    </p>
                )
            }
        },
        {
            field: "baseLocation",
            headerName: 'Location',
            type: "string",
            flex: 0.7, minWidth: 90,
            renderCell: ({ row, value }: GridValueGetterParams) => {
                return (
                    <>
                        {(row.baseLocation) ? <>{row.baseLocation.city}</> : "-"}
                    </>
                )
            }
        },
        {
            field: "primaryPhone",
            headerName: 'Phone',
            type: "string",
            flex: 0.8, minWidth: 110,
            renderCell: ({ row, value }: GridValueGetterParams) => (
                <>
                    {row.primaryPhone && <> {row.primaryPhone || ''}</>}

                    {/* <Tooltip title={row.gst.lable}><InfoIcon style={{ fontSize: '12px', color: '#aaa' }}></InfoIcon></Tooltip> */}

                </>
            )
        },
        {
            field: "personalEmail",
            headerName: 'Email',
            type: "string",
            flex: 1.2, minWidth: 140,
            renderCell: ({ row, value }: GridValueGetterParams) => {
                return (<>
                    {row.personalEmail}
                </>);
            },
        },
        {
            field: "dateOfJoining",
            headerName: 'DOJ',
            type: "string",
            flex: 0.8, minWidth: 100,
            renderCell: ({ row, value }: GridValueGetterParams) => {
                return (<>
                    {(row.dateOfJoining && row.dateOfJoining != '') ? <>{moment(row.dateOfJoining).format("DD MMM YYYY")}</> : "-"}
                </>);
            },
        },
        {
            field: "hasUserAccess",
            headerName: 'Status',
            type: "string",
            flex: 0.7, minWidth: 90,
            renderCell: ({ row }: GridValueGetterParams) => {
                if (row.hasUserAccess && row.userId) {
                    if (row.userId?.isDefaultPassword || row.isDefaultPassword) {
                        return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Chip label="Invited" size="small" style={{ color: '#e6a817', backgroundColor: '#fdf8e8', border: '1px solid #e6a817' }} />
                                {/* <IconButton size="small" title="Resend Invite"
                                    style={{ color: '#488dff', height: 28, width: 28 }}
                                    onClick={(e) => { e.stopPropagation(); handleResendInvite(row); }}>
                                    <EmailIcon style={{ fontSize: 16 }} />
                                </IconButton> */}
                            </Box>
                        );
                    }
                    return <Chip label="Active" size="small" style={{ color: '#49a563', backgroundColor: '#edf6ef', border: '1px solid #49a563' }} />;
                }
                return <Chip label="Not Invited" size="small" style={{ color: '#999', backgroundColor: '#f5f5f5', border: '1px solid #ddd' }} />;
            },
        },
        {
            field: "actions",
            headerName: 'Actions',
            type: "string",
            flex: 0.7, minWidth: 100,
            renderCell: ({ row, value }: GridValueGetterParams) => (
                <>
                    <IconButton
                        style={{ color: '#488dff', backgroundColor: '#fff', border: '0.1px solid #488dff', height: 35, width: 35, marginRight: 10 }}
                        size='small' onClick={() => handleViewEmployeeClick(row)}><RemoveRedEyeTwoToneIcon style={{ fontSize: 16 }} />

                    </IconButton>
                    <IconButton
                        style={{ color: '#49a563', backgroundColor: '#edf6ef', border: '0.1px solid #49a563', height: 35, width: 35, marginRight: 10 }}
                        size='small' onClick={() => handleEditEmployeeClick(row)}><EditIcon style={{ fontSize: 16 }} />

                    </IconButton>
                </>
            )
        },
        // {
        //  field: "actions",
        //  headerName: 'Actions',
        //  type: "actions",
        //  width:150,
        //     getActions: (params: GridValueGetterParams) => {
        //         const viewAction = (
        //             <GridActionsCellItem
        //                 icon={<RemoveRedEyeTwoToneIcon/>}
        //                 title="View"
        //                 label="View"
        //                 style={{ marginRight: '10px', color:'#488dff',backgroundColor:'#f1f0fc',border:'1px solid #488dff',borderRadius:10 }}
        //                 onClick={() => handleViewEmployeeClick(params.row)}
        //             />
        //         );

        //         const editAction = (
        //             <GridActionsCellItem
        //                 icon={<EditIcon/>}
        //                 title="Edit"
        //                 label="Edit"
        //                 style={{marginLeft:-10, marginRight: '3px', color:'#488dff',backgroundColor:'#f1f0fc',border:'1px solid #488dff',borderRadius:10 }}
        //                 onClick={() => handleEditEmployeeClick(params.row)} />
        //         );

        //         const deleteAction = (
        //         <GridActionsCellItem
        //             icon={<DeleteIcon/>}
        //             title="Delete"
        //             style={{ marginRight: '0px', color:'#c33b3b',backgroundColor:'#fbecec',border:'1px solid #c33b3b',borderRadius:10 }}
        //             label="Delete"
        //             disabled
        //             // onClick={deleteSelectedAddress(params.row)}
        //         />
        //         );

        //         const actions: JSX.Element[] = [];

        //         // Push edit action if user has update permission.
        //         actions.push(viewAction);
        //         if (canUpdateAccount) {
        //         actions.push(editAction);
        //         }
        //         // Push edit action if user has delete permission.
        //         // if (true) {
        //         // actions.push(deleteAction);
        //         // }
        //         return actions;
        //     }
        // }
    ], [
        // deleteSelected,
        // editSelected,
        //canUpdateAccount,
        // canDeleteAccount,
        //handleNameClick
    ]);

    const filterOnChangeHandler = (data: any) => {
        console.log("filter date from inventory listing ===== ", data);
        setPage(0);
        setPageQueryString('');
        setFilters(data);
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
        setPageQueryString(`&page=${newPage + 1}`);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPage(0);
        setPageSize(newPageSize);
        setPageQueryString('');
        setPageSizeQueryString(`&limit=${newPageSize}`);
    };

    return (
        <>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: '100%', pt: 2, pb: 1 }}>
                <Box sx={{ display: "flex", flex: 1 }}>
                    <EmployeeFilters
                        type={'employees'}
                        filterHandler={filterOnChangeHandler} />
                </Box>
                <Box sx={{ display: "flex", gap: 2, marginLeft: '10px' , marginRight: '10px' }}>
                    <Button
                        startIcon={<AddIcon />}
                        size='small'
                        onClick={handleAddEmployee}
                        style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7 }}
                        variant="contained"
                    >
                        New Employee
                    </Button>
                    {/* <Button
                        startIcon={<CurrencyRupeeIcon />}
                        size='small'
                        onClick={handleSalaryManagement}
                        style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7 }}
                        variant="outlined"
                    >
                        Salary Management
                    </Button> */}
                    <Button
                        startIcon={<EmailIcon />}
                        size='small'
                        onClick={handleInviteEmployee}
                        style={{ color: '#49a563', backgroundColor: '#fff', border: '0.5px solid #49a563', height: 35, borderRadius: 7 }}
                        variant="outlined"
                    >
                        Invite Employee
                    </Button>
                </Box>
            </Box>
            {/* Pending Invitations tab hidden for now */}

            <div style={{ height: "77vh", width: "100%", marginTop: '10px', overflow: 'auto', display: activeTab === 0 ? 'block' : 'none' }}>
                {(employees && employees.length > 0 || status === ApiStatus.LOADING) ? (
                    <CustomDataGrid
                        columns={columns}
                        rows={employees}
                        rowCount={rowCount}
                        rowsPerPageOptions={defaultRowsPerPageOptions}
                        page={page}
                        pageSize={pageSize}
                        onPageSizeChange={handlePageSizeChange}
                        onPageChange={handlePageChange}
                        checkboxSelection={true}
                        getRowId={(row) => row?._id || Math.random().toString()}
                        loading={status === ApiStatus.LOADING}
                    />
                ) : (
                    <Paper 
                        elevation={0} 
                        sx={{ 
                            height: '90%', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            backgroundColor: '#fafafb',
                            border: '2px dashed #e0e0e0',
                            borderRadius: 2,
                            p: 4
                        }}
                    >
                        <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
                            {/* Icon */}
                            <Box 
                                sx={{ 
                                    width: 120, 
                                    height: 120, 
                                    borderRadius: '50%', 
                                    backgroundColor: 'primary.light',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto',
                                    mb: 3
                                }}
                            >
                                <PeopleOutlineIcon sx={{ fontSize: 60, color: 'primary.main' }} />
                            </Box>
                            
                            {/* Title */}
                            <Typography 
                                variant="h5" 
                                sx={{ 
                                    fontWeight: 600, 
                                    color: '#333',
                                    mb: 1
                                }}
                            >
                                No Employees Yet
                            </Typography>
                            
                            {/* Description */}
                            <Typography 
                                variant="body1" 
                                sx={{ 
                                    color: '#666',
                                    mb: 4,
                                    lineHeight: 1.6
                                }}
                            >
                                Start building your team by adding your first employee. 
                                You can manage their information, track attendance, and more.
                            </Typography>
                            
                            {/* CTA Button */}
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                startIcon={<GroupAddIcon />}
                                onClick={handleAddEmployee}
                                sx={{
                                    px: 4,
                                    py: 2,
                                    borderRadius: 3,
                                    textTransform: 'none',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    letterSpacing: '0.5px',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        transform: 'translateY(-4px) scale(1.02)',
                                        boxShadow: (theme) => `0 8px 25px -8px ${theme.palette.primary.main}`,
                                        '&::before': {
                                            transform: 'translateX(0)',
                                        }
                                    },
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.2) 100%)',
                                        transform: 'translateX(-100%)',
                                        transition: 'transform 0.6s ease',
                                        zIndex: 1
                                    },
                                    '&:active': {
                                        transform: 'translateY(-2px) scale(0.98)',
                                    },
                                    '& .MuiButton-startIcon': {
                                        transition: 'transform 0.3s ease',
                                    },
                                    '&:hover .MuiButton-startIcon': {
                                        transform: 'rotate(360deg)',
                                    }
                                }}
                            >
                                Add Your First Employee
                            </Button>
                            
                            {/* Additional Help Text */}
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    display: 'block',
                                    mt: 3,
                                    color: '#999'
                                }}
                            >
                                Need help? Check our guide on employee management
                            </Typography>
                        </Box>
                    </Paper>
                )}
            </div>

            {/* Employee Invitation Dialog */}
            <EmployeeInviteDialog
                title="Invite Employee"
                open={inviteDialogOpen}
                onClose={handleInviteDialogClose}
            />
        </>
    );
}
export default EmployeeListData;
